import { memo, useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";

import { useHandleFormState } from "./hooks";

type Props = {
	// 支払い料金合計
	totalGuestCharge: number;
	// 返金処理の更新
	onUpdateReservationDetailUpdateRefundRequest: (refundPrice: number, refundReason: string) => Promise<void>;
};

export const ReservationDetailRefundRequest = memo(
	({ totalGuestCharge, onUpdateReservationDetailUpdateRefundRequest }: Props) => {
		const {
			refundPrice,
			refundPriceErrorMessage,
			refundReason,
			refundReasonErrorMessage,
			hasError,
			//
			handleChangeRefundPrice,
			handleChangeRefundReason,
		} = useHandleFormState();

		const [canOpenModal, setCanOpenModal] = useState(false);
		/**
		 * モーダルを表示
		 */
		const handleClickOpenModal = useCallback(() => {
			setCanOpenModal(true);
		}, []);
		/**
		 * モーダルを非表示
		 */
		const handleClickCloseModal = useCallback(() => {
			setCanOpenModal(false);
		}, []);
		/**
		 * モーダルの表示・非表示を制御
		 */
		const onChangeCanOpenModal = useCallback((open: boolean) => {
			setCanOpenModal(open);
		}, []);

		const handleOpenChange = useCallback(
			(open: boolean) => {
				onChangeCanOpenModal(open);
			},
			[onChangeCanOpenModal],
		);

		/**
		 * 返金処理の更新
		 */
		const handleClickUpdateReservationDetailRefundRequest = useCallback(() => {
			if (hasError || refundPrice === undefined || refundReason === undefined) return;

			onUpdateReservationDetailUpdateRefundRequest(refundPrice, refundReason).then(() =>
				onChangeCanOpenModal(false),
			);
		}, [hasError, onChangeCanOpenModal, onUpdateReservationDetailUpdateRefundRequest, refundPrice, refundReason]);

		/**
		 * 支払い料金合計テキスト
		 */
		const totalGuestChargeText = useMemo(() => `${totalGuestCharge.toLocaleString()} 円`, [totalGuestCharge]);
		/**
		 * 返金後の支払い料金合計テキスト
		 */
		const willTotalGuestChargeAfterRefundPriceText = useMemo(() => {
			const willTotalGuestChargeAfterRefundPrice = totalGuestCharge - (refundPrice ?? 0);

			return `${willTotalGuestChargeAfterRefundPrice.toLocaleString()} 円`;
		}, [refundPrice, totalGuestCharge]);

		/**
		 * お支払い料金合計よりも入力された金額が多い場合はエラーとみなす
		 */
		const disabled = useMemo(
			() => hasError || (refundPrice !== undefined && refundPrice > totalGuestCharge),
			[hasError, refundPrice, totalGuestCharge],
		);

		/**
		 * モーダルを表示するボタン
		 */
		if (!canOpenModal) {
			return (
				<Button variant="outline" type="button" size="sm" onClick={handleClickOpenModal}>
					返金の申請
				</Button>
			);
		}

		return (
			<Dialog open={canOpenModal} onOpenChange={handleOpenChange}>
				<DialogContent>
					<DialogHeader className="border-b border-b-zinc-200 pb-4">
						<DialogTitle>返金処理の申請</DialogTitle>
					</DialogHeader>

					<div className="space-y-4 p-2">
						{/* 注意事項 */}
						<p className="text-sm  text-error-600">
							※返金処理は、<b>１度</b>までとなっております。
							<br />
							CHILLNNでの返金処理完了後、再度返金が必要になった場合は、施設様とお客様間で直接やりとりしていただく必要があります。
						</p>

						<div className="flex items-center justify-between">
							<div className="text-center">
								<p className="text-sm font-semibold">支払い料金合計</p>

								<p>{totalGuestChargeText}</p>
							</div>

							<div>→</div>

							<div className="text-center">
								<p className="text-sm font-semibold">返金処理後</p>

								<p>{willTotalGuestChargeAfterRefundPriceText}</p>
							</div>
						</div>

						{/* 返金希望金額 */}
						<div className="flex items-end gap-x-3">
							<div className="min-w-60 max-w-60">
								<InputNumberField
									label="返金希望金額"
									value={refundPrice}
									handleInput={handleChangeRefundPrice}
									errorMessage={refundPriceErrorMessage}
								/>
							</div>

							<span className="text-sm font-semibold">円</span>
						</div>

						{/* 返金理由 */}
						<TextAreaField
							label="返金理由"
							value={refundReason}
							handleChange={handleChangeRefundReason}
							rows={3}
							errorMessage={refundReasonErrorMessage}
						/>
					</div>

					<div className="flex items-center justify-end gap-x-3 border-t border-t-zinc-200 px-6 pt-4">
						<Button variant="outline" type="button" onClick={handleClickCloseModal}>
							戻る
						</Button>

						<Button
							type="button"
							disabled={disabled}
							onClick={handleClickUpdateReservationDetailRefundRequest}
						>
							申請
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		);
	},
);
